// src/resources/customerRfm/FlowsPanel.tsx
//
// "Movimentação nos últimos 30 dias" (spec §6.2 item 4): os maiores fluxos entre
// segmentos, separados em quem subiu e quem caiu, mais novos e quem saiu da base.
// Clicar num fluxo filtra a lista por previousSegment + segment.
import React, { useMemo } from 'react';
import { Box, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { __t } from '../../i18n/t';
import { COLORS, cardSx, fmtNum } from '../dashboards/shared';
import { RfmFlow, RfmLeft, RfmSegment, SEGMENT_META, flowIsUp } from './segments';

interface Props {
  flows: RfmFlow[];
  left: RfmLeft[];
  newCustomers: number;
  filteredByPlace: boolean;
  activeSegment?: RfmSegment;
  activePreviousSegment?: RfmSegment | 'new';
  onSelectFlow: (flow: RfmFlow) => void;
  onSelectNew: () => void;
}

const FlowRow: React.FC<{ flow: RfmFlow; active: boolean; onClick: () => void }> = ({ flow, active, onClick }) => {
  const fromMeta = flow.from ? SEGMENT_META[flow.from] : null;
  const toMeta = SEGMENT_META[flow.to];
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
        px: 1, py: 0.6, borderRadius: 1.5, cursor: 'pointer',
        bgcolor: active ? 'rgba(120,105,223,0.10)' : 'transparent',
        '&:hover': { bgcolor: 'rgba(120,105,223,0.06)' },
      }}
    >
      {/* i18n: template literal dentro de ternário não é embrulhado pelo plugin de build,
          e o rótulo do segmento (meta.label) precisa passar por __t pra usar a tradução já
          existente no catálogo (segments.ts só traduz o rótulo em suggestedTagName). */}
      <Typography sx={{ fontSize: 12.5, color: COLORS.ink }}>
        {__t('%{from} → %{to}', {
          from: fromMeta ? `${fromMeta.emoji} ${__t(fromMeta.label)}` : __t('🆕 Novo'),
          to: `${toMeta.emoji} ${__t(toMeta.label)}`,
        })}
      </Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: COLORS.ink }}>{fmtNum(flow.customers)}</Typography>
    </Box>
  );
};

const FlowsPanel: React.FC<Props> = ({
  flows, left, newCustomers, filteredByPlace, activeSegment, activePreviousSegment, onSelectFlow, onSelectNew,
}) => {
  const { up, down } = useMemo(() => {
    const named = flows.filter((f) => f.from !== null);
    const sorted = [...named].sort((a, b) => b.customers - a.customers);
    return {
      up: sorted.filter((f) => flowIsUp(f.from, f.to)).slice(0, 5),
      down: sorted.filter((f) => !flowIsUp(f.from, f.to)).slice(0, 5),
    };
  }, [flows]);

  const leftTotal = left.reduce((acc, l) => acc + l.customers, 0);
  const isFlowActive = (flow: RfmFlow) =>
    activeSegment === flow.to && activePreviousSegment === (flow.from ?? 'new');
  const isNewActive = activePreviousSegment === 'new' && !activeSegment;

  return (
    <Box sx={{ ...cardSx }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>Movimentação nos últimos 30 dias</Typography>
      <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>Quem subiu, quem caiu, quem chegou e quem saiu.</Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: COLORS.success, mb: 0.5 }}>▲ SUBIRAM</Typography>
          {up.length ? up.map((f, i) => (
            <FlowRow key={i} flow={f} active={isFlowActive(f)} onClick={() => onSelectFlow(f)} />
          )) : <Typography sx={{ fontSize: 12, color: COLORS.faint }}>Sem movimentação de subida.</Typography>}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: COLORS.danger, mb: 0.5 }}>▼ CAÍRAM</Typography>
          {down.length ? down.map((f, i) => (
            <FlowRow key={i} flow={f} active={isFlowActive(f)} onClick={() => onSelectFlow(f)} />
          )) : <Typography sx={{ fontSize: 12, color: COLORS.faint }}>Sem movimentação de queda.</Typography>}
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box
          onClick={onSelectNew}
          sx={{
            cursor: 'pointer', borderRadius: 1.5, px: 1, py: 0.5,
            bgcolor: isNewActive ? 'rgba(120,105,223,0.10)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(120,105,223,0.06)' },
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: COLORS.ink }}>
            {/* Espaço entre rótulo e número por margem, não por texto: o plugin de i18n
                apara o espaço solto antes de uma expressão e o número grudava no rótulo. */}
            <Box component="span" sx={{ mr: 0.5 }}>🆕 Novos:</Box><b>{fmtNum(newCustomers)}</b>
          </Typography>
        </Box>
        <Tooltip title={filteredByPlace ? 'Com filtro de posto, quem saiu da base não aparece.' : 'Não abasteceram nos últimos 12 meses.'} arrow>
          <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
            <Box component="span" sx={{ mr: 0.5 }}>📤 Saíram (sem abastecer há mais de 12 meses):</Box><b>{fmtNum(leftTotal)}</b>
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default FlowsPanel;
